import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Landmark, Plus, Ban, RotateCcw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SearchableSelect } from "@/components/SearchableSelect";
import { API } from "@/context/AuthContext";
import { inrShort } from "@/pages/Dashboard";

export function FinancierSettings() {
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/financiers`).then((r) => setRows(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    axios.get(`${API}/ledger/ap/parties`).then((r) => setVendors(r.data)).catch(() => {});
  }, [load]);

  const setMode = async (code, mode) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/financiers`, { vendor_code: code, mode });
      toast.success(mode === "include" ? "Vendor added to the financier list" : "Vendor excluded from the financier list");
      setPick("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); } finally { setBusy(false); }
  };
  const clear = async (code) => {
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/financiers/${code}`);
      toast.success("Back to automatic detection for this vendor");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); } finally { setBusy(false); }
  };

  const listed = new Set(rows.map((r) => r.code));
  const source = (r) => (r.manual && r.auto ? "Auto + Manual" : r.manual ? "Manual" : "Auto");

  return (
    <Card className="border-slate-200 shadow-sm fade-in-up" data-testid="financier-settings-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2"><Landmark className="h-5 w-5 text-violet-600" />Financiers (TReDS / LC / buyer credit)</CardTitle>
            <p className="text-xs text-slate-500 mt-1">Vendors treated as financiers in the AP Ageing funded-exposure card, Dashboard strip, tags and exports. Auto = detected from SAP SU financing postings or a bank/TReDS-like name. Excluding an auto-detected vendor removes it everywhere.</p>
          </div>
          <div className="flex items-center gap-2">
            <SearchableSelect
              options={vendors.filter((v) => !listed.has(v.code)).map((v) => ({ value: v.code, label: v.name, sublabel: v.code }))}
              value={pick} onChange={setPick} placeholder="Add a vendor as financier…" width="w-72" testId="financier-add-select" />
            <Button size="sm" disabled={!pick || busy} onClick={() => setMode(pick, "include")} className="bg-violet-700 hover:bg-violet-800 text-white" data-testid="financier-add-button"><Plus className="h-3.5 w-3.5 mr-1" />Add</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <table className="w-full text-sm" data-testid="financier-settings-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="text-left py-2 pr-3">Vendor</th>
              <th className="text-left py-2 pr-3">Source</th>
              <th className="text-right py-2 pr-3">Open AP</th>
              <th className="text-left py-2 pr-3">Status</th>
              <th className="text-right py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.code} className={`border-b border-slate-100 ${r.active ? "" : "opacity-60"}`} data-testid={`financier-row-${r.code}`}>
                <td className="py-2 pr-3"><span className="font-medium text-slate-800">{r.name || r.code}</span> <span className="font-mono text-xs text-slate-400">{r.code}</span></td>
                <td className="py-2 pr-3 text-xs text-slate-600">{source(r)}</td>
                <td className="py-2 pr-3 text-right tabular-nums text-slate-700">{inrShort(r.open_total)}</td>
                <td className="py-2 pr-3">
                  <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${r.active ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-500"}`} data-testid={`financier-status-${r.code}`}>{r.active ? "Financier" : "Excluded"}</span>
                </td>
                <td className="py-2 text-right">
                  {r.excluded || r.manual
                    ? <Button size="sm" variant="outline" disabled={busy} onClick={() => clear(r.code)} className="h-7 text-xs" data-testid={`financier-reset-${r.code}`}><RotateCcw className="h-3 w-3 mr-1" />{r.excluded ? "Restore" : "Remove"}</Button>
                    : <Button size="sm" variant="outline" disabled={busy} onClick={() => setMode(r.code, "exclude")} className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50" data-testid={`financier-exclude-${r.code}`}><Ban className="h-3 w-3 mr-1" />Exclude</Button>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-sm text-slate-400">No financiers detected yet — sync SAP data or add one above.</td></tr>}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
